import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Megaphone,
  PenTool,
  Printer,
  Monitor,
  Footprints,
  Hammer,
  Phone,
  Mail,
  MapPin,
  Languages,
  GraduationCap,
  Heart,
  Download,
  Menu,
  X,
  ArrowUpRight,
  ArrowRight,
} from 'lucide-react';
import Reveal from '../components/Cv/Reveal';
import Showcase from '../components/Cv/Showcase';
import { StadiumMark, DotLeader, Divider } from '../components/Cv/Motif';
import {
  CV_FILE,
  profile,
  navSections,
  skillGroups,
  software,
  experience,
  showcase,
  education,
  interests,
  contact,
} from '../data/cvData';
import './Cv.css';

const ICONS = {
  megaphone: Megaphone,
  pen: PenTool,
  printer: Printer,
  monitor: Monitor,
  run: Footprints,
  hammer: Hammer,
};

/* -------------------------------------------------------------------------- */
/*  Barre de navigation                                                        */
/* -------------------------------------------------------------------------- */

function CvNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Section courante : on retient la dernière section dont le haut est passé
  // sous la barre. Plus stable qu'un simple « la plus visible » au scroll rapide.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-72px 0px -55% 0px', threshold: 0 }
    );

    navSections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`cv-nav ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="cv-shell cv-nav-inner">
        <a href="#top" className="cv-nav-brand" onClick={() => setOpen(false)}>
          <span className="cv-nav-brand-name">{profile.name}</span>
          <span className="cv-nav-brand-role">{profile.role}</span>
        </a>

        <nav className={`cv-nav-links ${open ? 'is-open' : ''}`} aria-label="Sections du CV">
          {navSections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={active === section.id ? 'is-active' : ''}
              onClick={() => setOpen(false)}
            >
              {section.label}
            </a>
          ))}
        </nav>

        <div className="cv-nav-actions">
          <a className="cv-btn cv-btn-primary cv-btn-sm" href={CV_FILE} download>
            <Download size={16} strokeWidth={1.75} />
            <span className="cv-btn-label">Télécharger le CV</span>
          </a>
          <button
            type="button"
            className="cv-nav-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={open}
          >
            {open ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Briques réutilisables                                                      */
/* -------------------------------------------------------------------------- */

function SectionHeading({ pill, title, centered }) {
  return (
    <Reveal className={`cv-heading ${centered ? 'is-centered' : ''}`}>
      <span className="cv-pill">{pill}</span>
      <h2 className="cv-h2">{title}</h2>
    </Reveal>
  );
}

/** Un lien qui reste interne quand c'est une route du site. */
function CardLink({ item }) {
  const label = (
    <>
      {item.linkLabel || item.label}
      <ArrowUpRight size={16} strokeWidth={1.75} />
    </>
  );

  if (item.internal) {
    return (
      <Link to={item.href} className="cv-card-link">
        {label}
      </Link>
    );
  }
  return (
    <a href={item.href} className="cv-card-link" download={item.download ? '' : undefined}>
      {label}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function Cv() {
  // Page autonome : elle porte son propre titre d'onglet.
  useEffect(() => {
    const previous = document.title;
    document.title = `${profile.name} · ${profile.role}`;
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="cv">
      <CvNav />

      {/* ---------------------------------------------------------- Hero */}
      <header className="cv-hero" id="top">
        <StadiumMark className="cv-hero-watermark" />
        <div className="cv-shell cv-hero-inner">
          <div className="cv-hero-text">
            <Reveal as="p" className="cv-hero-role">
              {profile.role}
            </Reveal>

            <Reveal as="h1" className="cv-hero-name" delay={0.05}>
              {profile.name}
            </Reveal>

            <Reveal delay={0.08}>
              <DotLeader />
            </Reveal>

            <Reveal as="p" className="cv-hero-pitch" delay={0.12}>
              {profile.positioning}
            </Reveal>

            <Reveal className="cv-hero-actions" delay={0.2}>
              {profile.ctas.map((cta) => (
                <a
                  key={cta.href}
                  href={cta.href}
                  className={`cv-btn cv-btn-${cta.variant}`}
                  download={cta.download ? '' : undefined}
                >
                  {cta.label}
                  {cta.download ? (
                    <Download size={16} strokeWidth={1.75} />
                  ) : (
                    <ArrowRight size={16} strokeWidth={1.75} />
                  )}
                </a>
              ))}
            </Reveal>

            <Reveal className="cv-hero-meta" delay={0.26}>
              <span className="cv-meta-item">
                <MapPin size={15} strokeWidth={1.5} />
                {profile.location}
              </span>
              <span className="cv-meta-item">
                <Languages size={15} strokeWidth={1.5} />
                {profile.languages}
              </span>
              <span className="cv-meta-item">{profile.experienceYears}</span>
            </Reveal>
          </div>

          <Reveal className="cv-hero-aside" delay={0.1} y={32}>
            <figure className="cv-portrait">
              <img
                src={profile.photo}
                alt={profile.photoAlt}
                className="cv-portrait-img"
                loading="eager"
              />
            </figure>

            {/* Rail de contact : la colonne d'icônes bleu nuit du CV imprimé. */}
            <div className="cv-rail">
              <a className="cv-rail-item" href={contact.phoneHref} aria-label="Téléphone">
                <Phone size={17} strokeWidth={1.75} />
              </a>
              <span className="cv-rail-dot" aria-hidden="true" />
              <a className="cv-rail-item" href={`mailto:${contact.email}`} aria-label="E-mail">
                <Mail size={17} strokeWidth={1.75} />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="cv-shell">
          <Reveal className="cv-status" delay={0.3}>
            <span className="cv-status-dot" aria-hidden="true" />
            <span className="cv-status-label">{profile.status.label}</span>
            <span className="cv-status-value">{profile.status.value}</span>
          </Reveal>
        </div>
      </header>

      
      
      {/* ------------------------------------------------------ Expériences */}
      <section className="cv-section cv-section-alt" id="experiences">
        <div className="cv-shell">
          <SectionHeading pill="Expériences" title="Parcours professionnel" />

          <ol className="cv-timeline">
            {experience.map((job, i) => (
              <Reveal
                as="li"
                className="cv-tl-item"
                key={`${job.org}-${job.period}`}
                delay={i * 0.04}
              >
                <div className="cv-tl-marker" aria-hidden="true">
                  <span className={`cv-tl-dot ${job.current ? 'is-current' : ''}`} />
                </div>
                <div className="cv-tl-body">
                  <p className="cv-tl-period">{job.period}</p>
                  <h3 className="cv-tl-role">{job.role}</h3>
                  <p className="cv-tl-org">
                    {job.org}
                    {job.contract && <span className="cv-tl-contract">{job.contract}</span>}
                  </p>
                  <ul className="cv-tl-bullets">
                    {job.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                  <ul className="cv-tags">
                    {job.tags.map((t) => (
                      <li key={t} className="cv-tag">
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------------------------------------------------- Réalisations */}
      <section className="cv-section cv-realisations" id="realisations">
        <div className="cv-shell">
          <SectionHeading pill="Portfolio" title="Mes réalisations" />
        </div>
        <Showcase items={showcase} />
      </section>

      {/* ------------------------------------------------------ Compétences */}
      <section className="cv-section cv-section-alt" id="competences">
        <div className="cv-shell">
          <SectionHeading pill="Compétences" title="Domaines d’expertise" />

          <div className="cv-skills">
            {skillGroups.map((group, i) => {
              const Icon = ICONS[group.icon] || PenTool;
              return (
                <Reveal className="cv-skill-card" key={group.title} delay={i * 0.05}>
                  <span className="cv-skill-icon">
                    <Icon size={18} strokeWidth={1.6} />
                  </span>
                  <h3 className="cv-skill-title">{group.title}</h3>
                  <ul className="cv-skill-list">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="cv-tools" delay={0.1}>
            <h3 className="cv-label">Boîte à outils</h3>
            <ul className="cv-tool-list">
              {software.map((s) => (
                <li key={s.name} className="cv-tool">
                  <span className="cv-tool-badge" style={{ '--tool': s.color }}>
                    {s.abbr}
                  </span>
                  <span className="cv-tool-text">
                    <span className="cv-tool-name">{s.name}</span>
                    <span className="cv-tool-level">{s.level}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>


      {/* ------------------------------------------------------- Formation */}
      <section className="cv-section" id="formation">
        <div className="cv-shell">
          <SectionHeading pill="Formation" title="Diplômes et formations" />

          <div className="cv-edu">
            <div className="cv-edu-col">
              <h3 className="cv-label">
                <GraduationCap size={16} strokeWidth={1.5} />
                Cursus
              </h3>
              <ul className="cv-edu-list">
                {education.map((e, i) => (
                  <Reveal as="li" key={e.title} delay={i * 0.05}>
                    <span className="cv-edu-period">{e.period}</span>
                    <span className="cv-edu-title">{e.title}</span>
                    <span className="cv-edu-school">{e.school}</span>
                  </Reveal>
                ))}
              </ul>
            </div>

            <div className="cv-edu-col">
              <h3 className="cv-label">
                <Heart size={16} strokeWidth={1.5} />
                Centres d’intérêt
              </h3>
              <ul className="cv-interests">
                {interests.map((it, i) => {
                  const Icon = ICONS[it.icon] || Heart;
                  return (
                    <Reveal as="li" key={it.label} delay={i * 0.05} className="cv-interest">
                      <span className="cv-interest-icon">
                        <Icon size={17} strokeWidth={1.6} />
                      </span>
                      {it.label}
                    </Reveal>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- Contact */}
      <section className="cv-section cv-contact" id="contact">
        <div className="cv-shell">
          <Divider />
          <Reveal className="cv-contact-inner">
            <span className="cv-pill">{contact.pill}</span>
            <h2 className="cv-h2 cv-contact-title">{contact.title}</h2>
            <p className="cv-contact-intro">{contact.intro}</p>

            <a href={`mailto:${contact.email}`} className="cv-contact-mail">
              {contact.email}
            </a>

            <ul className="cv-contact-list">
              <li>
                <Phone size={15} strokeWidth={1.5} />
                <a href={contact.phoneHref}>{contact.phone}</a>
              </li>
              <li>
                <MapPin size={15} strokeWidth={1.5} />
                <span>{contact.city}</span>
              </li>
            </ul>

            <div className="cv-contact-links">
              {contact.links.map((l) => (
                <CardLink key={l.href} item={l} />
              ))}
            </div>
          </Reveal>

          <footer className="cv-footer">
            <a
              className="cv-footer-site"
              href={contact.siteHref}
              target="_blank"
              rel="noreferrer"
            >
              {contact.site}
            </a>
            <p>
              {profile.name}, {profile.role}. {profile.location}.
            </p>
          </footer>
        </div>
      </section>
    </div>
  );
}
